// Empire Apps Chassis · identity-zkp · Aadhaar zero-knowledge predicates
// Wraps the existing aadhaar-offline-kyc connector and produces ZKP-friendly
// attribute commitments + predicate proofs. The raw Aadhaar number is never
// even present in the offline payload — the connector only exposes ref id +
// last 4 digits + signed demographics. We commit to demographics and prove
// predicates over them.

import type {
  AttributeValue,
  Predicate,
  PredicateProof,
  DIDString,
  Ed25519PrivateKey,
  Ed25519PublicKey,
} from './types.ts';
import { bbsSign, bbsDerive, bbsVerifyDerived, type BBSSignedCredential, type BBSDerivedProof } from './bbs-plus.ts';
import { deriveBundle, type DisclosureBundle } from './selective-disclosure.ts';
import { issueCredential, type IssueCredentialOptions } from './vc.ts';

export interface AadhaarOfflinePayload {
  readonly referenceId: string;
  readonly aadhaarLast4: string;
  readonly name: string;
  readonly dob: string; // YYYY-MM-DD
  readonly gender: 'M' | 'F' | 'T';
  readonly addressLine: string;
  readonly state: string;
  readonly pincode: string;
  readonly photoB64?: string;
  readonly signatureValid: boolean;
  readonly issuedAt: string;
}

export interface AadhaarZkpAttributes {
  readonly name: string;
  readonly dob_year: number;
  readonly age_now: number;
  readonly age_gte_18: boolean;
  readonly age_gte_21: boolean;
  readonly age_gte_60: boolean;
  readonly gender: string;
  readonly state: string;
  readonly state_code: string;
  readonly pincode_prefix3: string;
  readonly indian_resident: boolean;
  readonly aadhaar_last4: string;
  readonly reference_id_hash: string;
}

// State code mapping covers all 28 + 8 + 1 — the first two digits of pincode
// uniquely identify the postal region, which is what verifiers actually need.
const STATE_CODE_BY_NAME: Record<string, string> = {
  'andhra pradesh': 'AP', 'arunachal pradesh': 'AR', 'assam': 'AS', 'bihar': 'BR',
  'chhattisgarh': 'CT', 'goa': 'GA', 'gujarat': 'GJ', 'haryana': 'HR',
  'himachal pradesh': 'HP', 'jharkhand': 'JH', 'karnataka': 'KA', 'kerala': 'KL',
  'madhya pradesh': 'MP', 'maharashtra': 'MH', 'manipur': 'MN', 'meghalaya': 'ML',
  'mizoram': 'MZ', 'nagaland': 'NL', 'odisha': 'OR', 'punjab': 'PB',
  'rajasthan': 'RJ', 'sikkim': 'SK', 'tamil nadu': 'TN', 'telangana': 'TG',
  'tripura': 'TR', 'uttarakhand': 'UT', 'uttar pradesh': 'UP', 'west bengal': 'WB',
  'andaman and nicobar islands': 'AN', 'chandigarh': 'CH',
  'dadra and nagar haveli and daman and diu': 'DH', 'delhi': 'DL',
  'jammu and kashmir': 'JK', 'ladakh': 'LA', 'lakshadweep': 'LD', 'puducherry': 'PY',
};

export function aadhaarToZkpAttributes(payload: AadhaarOfflinePayload, asOf?: Date): AadhaarZkpAttributes {
  const ref = asOf ?? new Date();
  const dob = new Date(payload.dob);
  const dobYear = dob.getUTCFullYear();
  const age = computeAge(dob, ref);
  const stateLower = payload.state.toLowerCase().trim();
  const stateCode = STATE_CODE_BY_NAME[stateLower] ?? 'XX';
  return {
    name: payload.name,
    dob_year: dobYear,
    age_now: age,
    age_gte_18: age >= 18,
    age_gte_21: age >= 21,
    age_gte_60: age >= 60,
    gender: payload.gender,
    state: payload.state,
    state_code: stateCode,
    pincode_prefix3: (payload.pincode || '').slice(0, 3),
    indian_resident: true,
    aadhaar_last4: payload.aadhaarLast4,
    reference_id_hash: payload.referenceId.slice(0, 8),
  };
}

function computeAge(dob: Date, ref: Date): number {
  let years = ref.getUTCFullYear() - dob.getUTCFullYear();
  const m = ref.getUTCMonth() - dob.getUTCMonth();
  if (m < 0 || (m === 0 && ref.getUTCDate() < dob.getUTCDate())) years--;
  return years;
}

export async function aadhaarSign(
  payload: AadhaarOfflinePayload,
  issuerSk: Ed25519PrivateKey,
  issuerPk: Ed25519PublicKey,
  asOf?: Date,
): Promise<BBSSignedCredential> {
  const attrs = aadhaarToZkpAttributes(payload, asOf);
  const flat: Record<string, AttributeValue> = {
    name: attrs.name,
    dob_year: attrs.dob_year,
    age_now: attrs.age_now,
    age_gte_18: attrs.age_gte_18,
    age_gte_21: attrs.age_gte_21,
    age_gte_60: attrs.age_gte_60,
    gender: attrs.gender,
    state: attrs.state,
    state_code: attrs.state_code,
    pincode_prefix3: attrs.pincode_prefix3,
    indian_resident: attrs.indian_resident,
    aadhaar_last4: attrs.aadhaar_last4,
    reference_id_hash: attrs.reference_id_hash,
  };
  return bbsSign(flat, issuerSk, issuerPk);
}

export const AADHAAR_PREDICATE_LIBRARY = {
  ageGte18: { attribute: 'age_gte_18', op: 'eq', target: true } as Predicate,
  ageGte21: { attribute: 'age_gte_21', op: 'eq', target: true } as Predicate,
  ageGte60: { attribute: 'age_gte_60', op: 'eq', target: true } as Predicate,
  indianResident: { attribute: 'indian_resident', op: 'eq', target: true } as Predicate,
  fromState: (code: string): Predicate => ({ attribute: 'state_code', op: 'eq', target: code }),
  fromAnyState: (codes: readonly string[]): Predicate => ({ attribute: 'state_code', op: 'in', target: codes }),
  pincodePrefix: (prefix: string): Predicate => ({ attribute: 'pincode_prefix3', op: 'eq', target: prefix }),
  dobYearLte: (year: number): Predicate => ({ attribute: 'dob_year', op: 'lte', target: year }),
  dobYearGte: (year: number): Predicate => ({ attribute: 'dob_year', op: 'gte', target: year }),
} as const;

export async function aadhaarDeriveBundle(
  cred: BBSSignedCredential,
  predicates: readonly Predicate[],
): Promise<DisclosureBundle> {
  return deriveBundle(cred, predicates);
}

export async function aadhaarVerifyDerived(proof: BBSDerivedProof): Promise<boolean> {
  const r = await bbsVerifyDerived(proof);
  return r.ok;
}

export async function aadhaarIssueVC(
  payload: AadhaarOfflinePayload,
  issuer: DIDString,
  subject: DIDString,
  issuerSk: Ed25519PrivateKey,
): Promise<Awaited<ReturnType<typeof issueCredential>>> {
  const attrs = aadhaarToZkpAttributes(payload);
  const opts: IssueCredentialOptions = {
    issuer,
    issuerPrivateKey: issuerSk,
    subject,
    type: ['AadhaarDemographicCredential'],
    claims: {
      ...attrs,
    },
  };
  return issueCredential(opts);
}
